import { useScrollLock, useSheetDrag } from "./ui";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, PlanView } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { CalendarIcon, CloseIcon, PlusIcon } from "./Icons";

const DAY = 86_400_000;
const dateShort = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dayInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

interface Row { id?: string; name: string; page: number | ""; date: string | null; key: number }

/**
 * The organizer plans a stretch: named landmarks by page, dates that follow
 * the club's pace unless set by hand, and what the meeting covers. Suggested
 * pages are starting points to overtype with the nearest chapter break.
 */
export function PlanSheet({ clubId, book, plan, meetingAt, usualPace, onClose }:
  { clubId: string; book: ClubBook; plan: PlanView; meetingAt: string | null; usualPace: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [pages, setPages] = useState<number | "">(plan.pages ?? "");
  // Between stretches the sheet plans the next one, from where the last ended.
  const from = plan.ended && plan.to !== null ? plan.to : plan.from;
  const [rows, setRows] = useState<Row[]>(() => (plan.ended ? plan.landmarks.filter((l) => l.beyond) : plan.landmarks)
    .map((l, i) => ({ id: l.id, name: l.name, page: l.page, date: l.dateEdited ? dayInput(l.date) : null, key: i })));
  const [to, setTo] = useState<number | "whole">(plan.ended || plan.to === null ? "whole" : plan.to);
  const [custom, setCustom] = useState<number | "">("");
  // "another page" can be chosen before its page is typed; Save waits for a valid page.
  const [other, setOther] = useState(false);
  const customRef = useRef<HTMLInputElement>(null);
  const [editingDate, setEditingDate] = useState<number | null>(null);
  const nextKey = useRef(rows.length);
  const focusKey = useRef<number | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  const save = useMutation({
    mutationFn: () => api.savePlan(clubId, book.id, {
      landmarks: rows.filter((r) => r.page !== "").map((r) => ({ id: r.id, name: r.name, page: r.page as number, date: r.date || null })),
      to: to === "whole" ? null : to,
      ...(pages !== plan.pages ? { pages: pages === "" ? null : pages } : {}),
    }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["discussion", clubId, book.id] }); onClose(); },
  });

  useScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    if (focusKey.current === null) return;
    const el = dialog.current?.querySelector<HTMLInputElement>(`#lm-page-${focusKey.current}`);
    el?.focus(); el?.select(); focusKey.current = null;
  }, [rows.length]);

  const total = pages === "" ? null : pages;
  const end = to === "whole" ? total : to;
  const startAt = plan.boundaries.at(-1)?.metAt ? new Date(plan.boundaries.at(-1)!.metAt!) : book.startedAt ? new Date(book.startedAt) : new Date();
  const deadline = meetingAt && new Date(meetingAt) > new Date() ? new Date(meetingAt) : null;
  const days = deadline ? Math.max(1, (deadline.getTime() - startAt.getTime()) / DAY) : null;
  const pace = total && end && days ? Math.ceil((end - from) / days) : null;
  const paceToday = total && days ? Math.ceil((total - from) / days) : null; // whole book, for the subtitle
  const reachAtUsual = usualPace && days ? Math.round(from + usualPace * days) : null;

  function derivedDate(page: number): Date | null {
    if (!deadline || !end || end <= from) return null;
    const frac = Math.max(0, Math.min(1, (page - from) / (end - from)));
    return new Date(startAt.getTime() + frac * (deadline.getTime() - startAt.getTime()));
  }
  function suggest(): { page: number; lo: number; hi: number } | null {
    if (!total) return null;
    const pts = [from, ...rows.map((r) => r.page).filter((p): p is number => p !== ""), total].sort((a, b) => a - b);
    let best = { lo: from, hi: total, gap: -1 };
    for (let i = 1; i < pts.length; i++) { const gap = pts[i]! - pts[i - 1]!; if (gap > best.gap) best = { lo: pts[i - 1]!, hi: pts[i]!, gap }; }
    if (best.gap < 2) return null;
    return { page: Math.round((best.lo + best.hi) / 2), lo: best.lo, hi: best.hi };
  }
  function addRow() {
    const s = suggest();
    const key = nextKey.current++;
    focusKey.current = key;
    setRows((rs) => [...rs, { name: "", page: s?.page ?? "", date: null, key }]);
  }
  const sorted = [...rows].sort((a, b) => (a.page === "" ? 1 : b.page === "" ? -1 : a.page - b.page));
  const hint = suggest();
  const closest = reachAtUsual !== null && sorted.length ? sorted.reduce<Row | null>((b, r) => (r.page === "" ? b : !b || Math.abs(r.page - reachAtUsual) < Math.abs((b.page as number) - reachAtUsual) ? r : b), null) : null;
  const valid = total !== null && rows.every((r) => r.page !== "" && r.page > from && r.page <= total) && (other ? custom !== "" && custom > from && custom <= total : (to === "whole" || (to > from && to <= total)));

  useSheetDrag(dialog, onClose);
  // Rendered at the top of the page so it covers everything, the reading track included.
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal plan-sheet" role="dialog" aria-label="Plan the read" ref={dialog}>
        <div className="grab" />
        <div className="head"><b>{from > 0 ? `Plan this stretch · from p. ${from}` : "Plan the read"}</b><button type="button" aria-label="Close" onClick={onClose}><CloseIcon /></button></div>
        <div className="sub">
          <b>{book.book.title}</b>{total ? ` · ${total} pages` : ""}<br />
          {book.startedAt && from === 0 ? `Started ${dateShort(new Date(book.startedAt))}` : `From p. ${from}`}
          {deadline ? ` · meeting ${dateShort(deadline)}` : " · no meeting date yet."}
          {paceToday ? <> · <span className="pace">about {paceToday} pages a day</span>{usualPace ? `. Your usual is ${usualPace}.` : "."}</> : null}
          {!deadline && " Landmarks get dates once there is one."}
        </div>
        <form className="plan-list" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
          {total === null && (
            <label className="field"><span>How many pages is your copy?</span>
              <input id="plan-pages" className="input num" type="number" min={1} value={pages} onChange={(e) => setPages(e.target.value === "" ? "" : Number(e.target.value))} placeholder="275" /></label>
          )}
          {sorted.map((r, i) => {
            const d = r.date ? new Date(`${r.date}T12:00:00`) : r.page !== "" ? derivedDate(r.page) : null;
            const beyond = to !== "whole" && r.page !== "" && r.page > to;
            return (
              <div key={r.key} className={`lm${beyond ? " beyond" : ""}`}>
                <input id={`lm-name-${r.key}`} className="input" value={r.name} placeholder={`Part ${i + 1}`} maxLength={60} aria-label="Landmark name"
                  onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))} />
                <span className="pg">to p. <input id={`lm-page-${r.key}`} className="input num" type="number" min={from + 1} max={total ?? undefined} value={r.page} aria-label="Page"
                  onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, page: e.target.value === "" ? "" : Number(e.target.value) } : x)))} /></span>
                <div className="when">
                  <CalendarIcon width={14} height={14} />
                  {beyond ? <span>next stretch</span>
                    : editingDate === r.key ? (
                      <input className="input" type="date" value={r.date ?? (d ? d.toISOString().slice(0, 10) : "")} autoFocus aria-label="Date"
                        onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, date: e.target.value || null } : x)))} onBlur={() => setEditingDate(null)} />
                    ) : d ? <span>by <b>{dateShort(d)}</b> · {r.date ? "set by hand" : "from your pace"}</span> : <span>no date yet</span>}
                  {!beyond && deadline && editingDate !== r.key && (
                    <button type="button" className="link" onClick={() => setEditingDate(r.key)}>{r.date ? "reset" : "edit"}</button>
                  )}
                  {r.date && editingDate !== r.key && <button type="button" className="link" onClick={() => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, date: null } : x)))}>use pace</button>}
                  <button type="button" className="x" aria-label="Remove landmark" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}><CloseIcon width={16} height={16} /></button>
                </div>
              </div>
            );
          })}
          {hint && rows.length > 0 && rows.at(-1)!.page === hint.page && (
            <div className="hint">Halfway between {hint.lo === from && from === 0 ? "the start" : `p. ${hint.lo}`} and {hint.hi === total ? "the end" : `p. ${hint.hi}`} is <b>p. {hint.page}</b>. Nearest chapter break? Overtype it.</div>
          )}
          {rows.length < 8 && total !== null && <button type="button" className="btn ghost add" onClick={addRow}><PlusIcon width={16} height={16} />Add a landmark</button>}

          <div className="covers">
            <div className="lbl">Meeting covers</div>
            <label className="radio"><input type="radio" name="covers" checked={!other && to === "whole"} onChange={() => { setOther(false); setTo("whole"); }} /><span>the whole book</span></label>
            {sorted.filter((r) => r.page !== "").map((r, i) => (
              <label key={r.key} className="radio"><input type="radio" name="covers" checked={!other && to === r.page} onChange={() => { setOther(false); setTo(r.page as number); }} />
                <span>through {r.name.trim() || `Part ${i + 1}`} <small>· p. {r.page}{closest?.key === r.key ? ` · closest to your usual ${usualPace} a day` : ""}</small></span></label>
            ))}
            <label className="radio"><input type="radio" name="covers" checked={other || (to !== "whole" && !sorted.some((r) => r.page === to))}
              onChange={() => { setOther(true); if (custom !== "") setTo(custom); customRef.current?.focus(); }} />
              <span className="fixed">another page</span> <input ref={customRef} className="input num" type="number" min={from + 1} max={total ?? undefined} value={custom} placeholder="—" aria-label="Page the meeting covers up to"
                onFocus={() => setOther(true)}
                onChange={(e) => { const v = e.target.value === "" ? "" : Number(e.target.value); setCustom(v); setOther(true); if (v !== "") setTo(v); }} /></label>
            {reachAtUsual !== null && total !== null && reachAtUsual < total && !closest && <p className="muted small">At your usual {usualPace} a day you'd reach about p. {reachAtUsual} by the meeting.</p>}
          </div>
          {pace !== null && to !== "whole" && <p className="muted small">That's about {pace} pages a day for this stretch.</p>}
          {save.isError && <p className="error">{describeError(save.error)}</p>}
          <div className="foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={!valid || save.isPending}>{save.isPending ? "Saving…" : "Save plan"}</button>
          </div>
        </form>
      </div>
    </>
  , document.body);
}
